import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment/environment';
import { TranslocoService } from '@jsverse/transloco';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { translateApiError } from 'app/core/utils/api-error';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AuthService, type AuthSessionResponse } from './auth.service';

export interface MfaEnrollResponse {
	id: string;
	totp: { qr_code: string; secret: string; uri?: string };
}

export interface MfaFactor {
	id: string;
	factor_type: string;
	status: string;
	friendly_name?: string;
}

/** Session tokens some MFA answers may carry (the upgraded, two-step session) */
type MaybeSession = Partial<Pick<AuthSessionResponse, 'access_token' | 'refresh_token'>>;

/**
 * Two-step verification (TOTP): enrolment from the profile and the code step of the login.
 * The session itself (tokens, user, navigation) stays in AuthService.
 */
@Injectable({
	providedIn: 'root',
})
export class MfaService {
	private readonly http = inject(HttpClient);
	private readonly authService = inject(AuthService);
	private readonly messageAlertService = inject(MessageAlertService);
	private readonly transloco = inject(TranslocoService);
	private readonly baseUrl = `${environment.pathBackend}/auth/mfa`;

	/** Shows errors.<code> from the backend or the fallback key */
	private fail(error: HttpErrorResponse, fallbackKey: string, toast = true): Observable<never> {
		const message = translateApiError(error, this.transloco, fallbackKey);
		if (toast)
			this.messageAlertService.showMessage('error', this.transloco.translate('common.labels.error'), message);
		return throwError(() => Object.assign(new Error(message), { code: error.error?.code as string | undefined }));
	}

	/** Starts enrolment: QR code and secret for the authenticator app */
	enroll(): Observable<MfaEnrollResponse> {
		return this.http
			.post<MfaEnrollResponse>(`${this.baseUrl}/enroll`, {})
			.pipe(catchError((error: HttpErrorResponse) => this.fail(error, 'authMessages.mfaEnrollError')));
	}

	/**
	 * Confirms enrolment with the first code. If the answer carries the upgraded (two-step) session, it
	 * replaces the current one: the old tokens would be refused once the account has a verified factor.
	 */
	verifyEnrollment(factorId: string, code: string): Observable<unknown> {
		return this.http.post<MaybeSession>(`${this.baseUrl}/verify`, { factorId, code }).pipe(
			tap((response) => {
				if (response?.access_token && response.refresh_token) {
					this.authService.persistTokens(response.access_token, response.refresh_token);
				}
			}),
			catchError((error: HttpErrorResponse) => this.fail(error, 'authMessages.invalidVerificationCode')),
		);
	}

	unenroll(factorId: string): Observable<unknown> {
		return this.http
			.post(`${this.baseUrl}/unenroll`, { factorId })
			.pipe(catchError((error: HttpErrorResponse) => this.fail(error, 'authMessages.mfaDisableError')));
	}

	/** No catchError: "could not load" is not "MFA disabled" (the toggle would offer to enroll again) */
	listFactors(): Observable<{ factors: MfaFactor[] }> {
		return this.http.get<{ factors: MfaFactor[] }>(`${this.baseUrl}/factors`);
	}

	/**
	 * Code step of the login. Uses the temporary token of the MFA challenge (kept by the login screen,
	 * never stored, so no other request goes out with it) and, on success, starts the session and goes
	 * back to the page that asked for the login.
	 * Errors carry `code` (e.g. TOO_MANY_REQUESTS) so the screen can tell them from a wrong code.
	 */
	verifyLogin(
		accessToken: string,
		factorId: string,
		code: string,
		trustDevice = false,
	): Observable<AuthSessionResponse> {
		return this.http
			.post<AuthSessionResponse>(`${this.baseUrl}/verify-login`, { accessToken, factorId, code, trustDevice })
			.pipe(
				tap((response) => {
					if (!response?.access_token) {
						throw new Error('No access token received after MFA verification');
					}
					this.authService.persistSession(response);
					this.authService.navigateAfterLogin();
				}),
				catchError((error: HttpErrorResponse) =>
					this.fail(error, 'authMessages.invalidVerificationCode', false),
				),
			);
	}
}
